import * as React from 'react';
import {cn} from 'src/lib/utils';

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export const Container = React.forwardRef<HTMLDivElement, DivProps>(
  ({className, ...props}, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-4', className)} {...props} />
  ),
);

export const PageTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({className, ...props}, ref) => (
    <h1 ref={ref} className={cn('text-2xl font-semibold', className)} {...props} />
  ),
);

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement>;

export const Header = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({className, ...props}, ref) => (
    <h2 ref={ref} className={cn('text-lg', className)} {...props} />
  ),
);

type DetailHeaderProps = {
  title: React.ReactNode;
  /** The page's headline number, rendered large beneath the title. */
  amount?: React.ReactNode;
  actions: React.ReactNode;
};

/**
 * The header every transaction-like detail page shares: the title with the
 * amount beneath it as the page's headline number, and the page's actions
 * on the right. The amount leads rather than sitting beside the buttons,
 * since it is the one fact every visit to the page is checking.
 */
export const DetailHeader = ({title, amount, actions}: DetailHeaderProps) => (
  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
    <div className="flex min-w-0 flex-col gap-1">
      <PageTitle className="break-words">{title}</PageTitle>
      {amount != null && (
        <div className="text-3xl tabular-nums">{amount}</div>
      )}
    </div>
    <div className="flex items-center gap-3">{actions}</div>
  </div>
);

/**
 * Header-row destructive actions are outlined rather than filled: the page's
 * loudest element should not be the one that deletes it. The confirm modal
 * (or the danger row's explanation) carries the weight instead.
 */
export const destructiveOutlineClass =
  'text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/15';
